import { useEffect, useMemo, useState } from "react";
import {
  CalendarClock,
  Check,
  FileSpreadsheet,
  LockKeyhole,
  RotateCcw,
  Search,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  X,
} from "lucide-react";
import { isSupabaseConfigured } from "../lib/supabase";
import { finalizeCycle, loadCenterStatuses, reopenCenter, type CenterStatusRow } from "../services/admin";
import "./AdminPanel.css";
import { setMonthEnabled, setRegistrationEnabled } from "../services/poi";
import { MONTHS, type WorkspaceData } from "../types";

interface Props {
  workspace: WorkspaceData;
  onWorkspaceChange: React.Dispatch<React.SetStateAction<WorkspaceData | null>>;
}

const STATUS_LABELS = {
  not_started: "No iniciada",
  draft: "Borrador",
  closed: "Propuesta enviada",
  reopened: "Reabierta",
  pending_gmr: "Pendiente GMR",
  pending_line: "Pendiente órgano de línea",
  observed: "Observada",
  accepted: "Aceptada",
} as const;

function demoRows(workspace: WorkspaceData): CenterStatusRow[] {
  return workspace.costCenters.map((center, index) => {
    const selected = center.id === workspace.selectedCenterId;
    return {
      centerId: center.id,
      code: center.code,
      name: center.name,
      submissionId: selected ? workspace.submissionId : null,
      status: selected ? workspace.submissionStatus : index === 1 ? "not_started" : "draft",
      changedTargets: selected
        ? workspace.tasks.reduce((total, task) => total + task.modified.filter((value) => value !== null).length, 0)
        : 0,
      closedAt: selected && workspace.submissionStatus === "closed" ? new Date().toISOString() : null,
      reopenedAt: null,
      reopenReason: null,
    };
  });
}

export function AdminPanel({ workspace, onWorkspaceChange }: Props) {
  const [rows, setRows] = useState<CenterStatusRow[]>(() => demoRows(workspace));
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [selected, setSelected] = useState<CenterStatusRow | null>(null);
  const [reason, setReason] = useState("");
  const [selectedMonths, setSelectedMonths] = useState<number[]>(
    workspace.cycle.months.filter((month) => month.enabled).map((month) => month.month),
  );
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    loadCenterStatuses(workspace.cycle.id, workspace.costCenters)
      .then(setRows)
      .catch((error: unknown) => setMessage(error instanceof Error ? error.message : "No se pudieron cargar los estados."))
      .finally(() => setLoading(false));
  }, [workspace.cycle.id, workspace.costCenters, workspace.selectedCenterId, workspace.submissionStatus]);

  const visibleRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("es");
    return rows.filter((row) => !query || `${row.code} ${row.name}`.toLocaleLowerCase("es").includes(query));
  }, [rows, search]);
  const blockingRows = rows.filter((row) => ["draft", "reopened", "pending_gmr", "pending_line", "observed"].includes(row.status));


  const closedCount = rows.filter((row) => row.status === "closed" || row.status === "accepted").length;
  const openCount = rows.filter((row) => ["draft", "reopened", "pending_gmr", "pending_line", "observed"].includes(row.status)).length;

  async function toggleRegistration() {
    if (workspace.cycle.status === "closed") return;
    const enabled = !workspace.cycle.registrationEnabled;
    if (isSupabaseConfigured) await setRegistrationEnabled(workspace.cycle.id, enabled);
    onWorkspaceChange((current) => current ? { ...current, cycle: { ...current.cycle, registrationEnabled: enabled } } : current);
  }

  async function toggleMonth(monthIndex: number) {
    const enabled = !workspace.cycle.months[monthIndex].enabled;
    if (workspace.cycle.status === "closed") return;
    if (isSupabaseConfigured) await setMonthEnabled(workspace.cycle.id, monthIndex + 1, enabled);
    onWorkspaceChange((current) => current ? {
      ...current,
      cycle: {
        ...current.cycle,
        months: current.cycle.months.map((month, index) => index === monthIndex ? { ...month, enabled } : month),
      },
    } : current);
  }

  function openReopenDialog(row: CenterStatusRow) {
    setSelected(row);
    setReason("");
    setSelectedMonths(workspace.cycle.months.filter((month) => month.enabled).map((month) => month.month));
    setMessage(null);
  }

  function toggleAllowedMonth(month: number) {
    setSelectedMonths((current) => current.includes(month)
      ? current.filter((item) => item !== month)
      : [...current, month].sort((a, b) => a - b));
  }

  async function confirmReopen() {
    if (!selected?.submissionId || reason.trim().length < 5 || !selectedMonths.length) return;
    setReopening(true);
    setMessage(null);
    try {
      if (isSupabaseConfigured) await reopenCenter(selected.submissionId, reason.trim(), selectedMonths);
      setRows((current) => current.map((row) => row.centerId === selected.centerId ? {
        ...row,
        status: "reopened",
        reopenedAt: new Date().toISOString(),
        reopenReason: reason.trim(),
        closedAt: null,
      } : row));
      if (selected.centerId === workspace.selectedCenterId) {
        onWorkspaceChange((current) => current ? {
          ...current,
          submissionStatus: "draft",
          allowedMonths: selectedMonths,
        } : current);
      }
      setSelected(null);
      setMessage(`Se reabrió la reprogramación de ${selected.code} · ${selected.name}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "No se pudo reabrir la reprogramación.");
    } finally {
      setReopening(false);
    }
  }

  async function confirmFinalize() {
    if (blockingRows.length || workspace.cycle.status === "closed") return;
    setFinalizing(true);
    setMessage(null);
    try {
      const batchNumber = isSupabaseConfigured ? await finalizeCycle(workspace.cycle.id) : 1;
      const officializedAt = new Date().toISOString();
      onWorkspaceChange((current) => current ? {
        ...current,
        cycle: {
          ...current.cycle,
          status: "closed",
          registrationEnabled: false,
          officialVersionNumber: batchNumber,
          officializedAt,
        },
      } : current);
      setFinalizeDialogOpen(false);
      setMessage("Proceso institucional cerrado. Cada tarea modificada recibió su siguiente número MOD.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "No se pudo cerrar el proceso institucional.");
    } finally {
      setFinalizing(false);
    }
  }

  return (
    <div className="page admin-page">
      <header className="page-header">
        <div><span className="eyebrow">CONFIGURACIÓN DEL PROCESO</span><h1>Administración</h1><p>Controla la ventana de registro y reabre centros cerrados cuando sea necesario.</p></div>
        <div className="admin-header-actions">
          <span className="admin-badge"><ShieldCheck />Acceso administrador</span>
          <button className="primary-button" title={blockingRows.length ? `${blockingRows.length} centros aún requieren atención` : undefined} onClick={() => setFinalizeDialogOpen(true)} disabled={workspace.cycle.status === "closed" || blockingRows.length > 0}><LockKeyhole />{workspace.cycle.status === "closed" ? "Modificaciones oficializadas" : "Cerrar proceso y oficializar"}</button>
        </div>
      </header>

      {message && <div className="admin-message"><Check size={16} />{message}</div>}

      <section className="admin-grid">
        <div className="admin-card wide">
          <div className="card-heading"><div className="heading-icon"><SlidersHorizontal /></div><div><h2>Ventana de registro</h2><p>El bloqueo general prevalece sobre cualquier reapertura individual.</p></div></div>
          <div className="setting-row"><div><strong>Permitir reprogramaciones</strong><span>Los responsables podrán guardar y enviar sus cambios.</span></div><button className={`switch ${workspace.cycle.registrationEnabled ? "on" : ""}`} onClick={() => void toggleRegistration()} disabled={workspace.cycle.status === "closed"}><span /></button></div>
        </div>
        <div className="admin-card stat-card"><Users /><strong>{openCount}</strong><span>Centros en registro</span></div>
        <div className="admin-card stat-card"><LockKeyhole /><strong>{closedCount}</strong><span>Centros listos</span></div>
      </section>

      <section className="admin-card months-card">
        <div className="card-heading"><div className="heading-icon"><Settings2 /></div><div><h2>Meses habilitados globalmente</h2><p>Un mes bloqueado aquí no podrá habilitarse en una reapertura individual.</p></div></div>
        <div className="month-grid">{MONTHS.map((month, index) => <button key={month} className={workspace.cycle.months[index]?.enabled ? "enabled" : ""} onClick={() => void toggleMonth(index)} disabled={workspace.cycle.status === "closed"}><span>{month}</span><small>{workspace.cycle.months[index]?.enabled ? "Habilitado" : "Bloqueado"}</small><div className="mini-switch"><i /></div></button>)}</div>
      </section>

      <section className="admin-card center-monitor-card">
        <div className="monitor-heading">
          <div className="card-heading"><div className="heading-icon"><FileSpreadsheet /></div><div><h2>Estado por centro de costo</h2><p>Selecciona un centro cerrado para autorizar una nueva edición.</p></div></div>
          <div className="search-box"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar centro de costo…" /></div>
        </div>
        <div className="center-table-wrap">
          <table className="center-status-table">
            <thead><tr><th>Centro de costo</th><th>Estado</th><th>Metas modificadas</th><th>Último envío</th><th>Acción</th></tr></thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.centerId}>
                  <td><strong>{row.code}</strong><span>{row.name}</span></td>
                  <td><span className={`center-status ${row.status}`}>{STATUS_LABELS[row.status]}</span></td>
                  <td>{row.changedTargets}</td>
                  <td>{row.closedAt ? new Intl.DateTimeFormat("es-PE", { dateStyle: "medium", timeStyle: "short" }).format(new Date(row.closedAt)) : "—"}</td>
                  <td>{workspace.cycle.status !== "closed" && (row.status === "closed" || row.status === "accepted") ? <button className="reopen-button" onClick={() => openReopenDialog(row)}><RotateCcw />Reabrir</button> : <span className="no-action">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && <div className="table-loading">Cargando estados…</div>}
          {!loading && !visibleRows.length && <div className="table-loading">No se encontraron centros.</div>}
        </div>
      </section>

      {finalizeDialogOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => !finalizing && setFinalizeDialogOpen(false)}>
          <div className="reopen-modal" role="dialog" aria-modal="true" aria-labelledby="finalize-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header"><div className="modal-icon"><LockKeyhole /></div><div><span className="eyebrow">CIERRE INSTITUCIONAL</span><h2 id="finalize-title">Oficializar modificaciones</h2></div><button onClick={() => setFinalizeDialogOpen(false)} disabled={finalizing}><X /></button></div>
            <div className="selected-center"><span>Proceso</span><strong>{workspace.cycle.name}</strong></div>
            <div className="reopen-warning"><LockKeyhole /><span>Este cierre bloqueará definitivamente el registro. Cada tarea modificada avanzará una versión respecto de su MOD vigente: por ejemplo, MOD 00 → MOD 01 o MOD 02 → MOD 03. Solo se oficializará el último envío aceptado.</span></div>
            <div className="finalize-summary"><strong>{closedCount} centros listos</strong><span>{rows.filter((row) => row.status === "not_started").length} centros sin cambios registrados</span></div>
            <div className="modal-actions"><button className="secondary-button" onClick={() => setFinalizeDialogOpen(false)} disabled={finalizing}>Cancelar</button><button className="primary-button" onClick={() => void confirmFinalize()} disabled={finalizing}><LockKeyhole />{finalizing ? "Cerrando proceso…" : "Confirmar y oficializar"}</button></div>
          </div>
        </div>
      )}

      {selected && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => !reopening && setSelected(null)}>
          <div className="reopen-modal" role="dialog" aria-modal="true" aria-labelledby="reopen-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header"><div className="modal-icon"><RotateCcw /></div><div><span className="eyebrow">AUTORIZACIÓN EXCEPCIONAL</span><h2 id="reopen-title">Reabrir reprogramación</h2></div><button onClick={() => setSelected(null)} disabled={reopening}><X /></button></div>
            <div className="selected-center"><span>Centro de costo</span><strong>{selected.code} · {selected.name}</strong></div>
            <label className="reason-field">Motivo de reapertura<textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Describe por qué se autoriza una nueva edición…" rows={3} /><small>Mínimo 5 caracteres. El motivo quedará registrado en la auditoría.</small></label>
            <div className="allowed-months"><div><strong>Meses que podrá modificar</strong><span>Solo se muestran disponibles los meses habilitados globalmente.</span></div><div className="modal-month-grid">{MONTHS.map((month, index) => {
              const globallyEnabled = workspace.cycle.months[index]?.enabled ?? false;
              const checked = selectedMonths.includes(index + 1);
              return <label key={month} className={`${checked ? "checked" : ""} ${!globallyEnabled ? "disabled" : ""}`}><input type="checkbox" checked={checked} disabled={!globallyEnabled} onChange={() => toggleAllowedMonth(index + 1)} /><span>{month}</span>{checked && <Check />}</label>;
            })}</div></div>
            <div className="reopen-warning"><CalendarClock /><span>Los cambios ya registrados se conservarán. El responsable deberá volver a cerrar la reprogramación.</span></div>
            <div className="modal-actions"><button className="secondary-button" onClick={() => setSelected(null)} disabled={reopening}>Cancelar</button><button className="primary-button" onClick={() => void confirmReopen()} disabled={reopening || reason.trim().length < 5 || !selectedMonths.length}><RotateCcw />{reopening ? "Reabriendo…" : "Confirmar reapertura"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
